import React, { useEffect, useState } from 'react';

import { Checkbox, Input, cssVariableStyles } from '@newsela/angelou';
import PropTypes from 'prop-types';

import { NUMERICAL_GRADE_BANDS, STATE_ABBREVIATIONS, STATE_NAMES, SUBJECTS_AREA_TO_PRIMARY } from '@client/utils/constants';

import { $clearFilters, $filtersContainer, $subFiltersContainer, $to, $yearInput, $yearInputContainer, $errorMessage, ERROR_COLOR } from './style';
import SearchInput from '../SearchInput';
import {
  TOGGLE_LIMIT_TO_NATIONAL,
  TOGGLE_ONLY_CROSSWALKS,
  SET_SEARCH,
  SET_FILTER_RANGE,
  SET_FILTER_SUBJECTS,
  SET_FILTER_GRADES,
  SET_FILTER_STATES,
  CLEAR_ALL_FILTERS
} from '../StandardsSelector';
import CustomSelect from './CustomSelect';

const { generateIconColorCss } = cssVariableStyles;

const SUBJECT_OPTIONS = Object.keys(SUBJECTS_AREA_TO_PRIMARY).map((key) => ({
  value: key,
  label: key,
}));

const GRADES = ['K', ...Object.values(NUMERICAL_GRADE_BANDS).map(String)];

// Helper function to convert grade display value to API value
const getGradeValue = (grade) => {
  if (grade === 'K') return 0;
  return grade;
};

const GRADE_OPTIONS = GRADES.map((grade) => ({
  value: getGradeValue(grade),
  label: grade,
}));

const STATE_OPTIONS = STATE_ABBREVIATIONS.map((abbr, index) => ({
  value: abbr,
  label: STATE_NAMES[index]
}));

const Filters = ({ dispatch, state }) => {
  const {
    search,
    limitToNational,
    onlyCrosswalks,
    adoptionYearRange = { from: '', to: '' },
    subjects = [],
    grades = [],
    states = [],
  } = state;

  const [pendingAdoptionYearRange, setPendingAdoptionYearRange] = useState(adoptionYearRange);

  const getSelectedLabel = (selected, singular, plural, placeholder) => {
    if (!selected || selected.length === 0) return placeholder;
    if (selected.length === 1) return `1 ${singular} selected`;
    return `${selected.length} ${plural} selected`;
  };

  const getRangeLabel = (range, placeholder) => {
    if (range.from && range.to) return `${range.from}-${range.to} range`;
    return placeholder;
  };

  // Validation helper functions
  const isValidYearRange = (range) => {
    const currentYear = new Date().getFullYear();
    const minYear = 1996;
    const maxYear = currentYear + 1;
    const from = parseInt(range.from, 10);
    const to = parseInt(range.to, 10);

    // If both are empty, it's valid (no filter applied)
    if (!range.from && !range.to) return { isValid: true, error: null };

    // If only one is filled, check if it's a valid 4-digit year
    if (!range.from || !range.to) {
      const singleValue = range.from || range.to;
      const singleYear = parseInt(singleValue, 10);

      if (singleValue && (!isNaN(singleYear) && (singleValue.length !== 4))) {
        return { isValid: false, error: 'Years must be 4 digits (e.g., 2025)' };
      }

      // Check year range for single value
      if (singleValue && !isNaN(singleYear)) {
        if (singleYear < minYear || singleYear > maxYear) {
          return { isValid: false, error: `Year must be between ${minYear} and ${maxYear}` };
        }
      }

      return { isValid: true, error: null };
    }

    // Check if both years are valid 4-digit numbers
    if (range.from && range.to) {
      // Check format first
      if (range.from.length !== 4 || range.to.length !== 4) {
        return { isValid: false, error: 'Years must be 4 digits (e.g., 2025)' };
      }

      // Check if they're valid numbers in valid range
      if (isNaN(from) || isNaN(to) || from < 1000 || from > 9999 || to < 1000 || to > 9999) {
        return { isValid: false, error: 'Years must be 4 digits (e.g., 2025)' };
      }

      // Check year range bounds
      if (from < minYear || from > maxYear || to < minYear || to > maxYear) {
        return { isValid: false, error: `Years must be between ${minYear} and ${maxYear}` };
      }

      // Check if earliest <= latest
      if (from > to) {
        return { isValid: false, error: 'Earliest year cannot be greater than latest year' };
      }

      return { isValid: true, error: null };
    }

    return { isValid: true, error: null };
  };

  const pendingValidation = isValidYearRange(pendingAdoptionYearRange);

  useEffect(() => {
    setPendingAdoptionYearRange(adoptionYearRange);
  }, [adoptionYearRange]);

  return (
    <div css={$filtersContainer}>
      <div css={{ display: 'flex' }}>
        <SearchInput
          value={search}
          onChange={(e) => dispatch({ type: SET_SEARCH, payload: e.target.value })}
        />
      </div>
      <div css={$subFiltersContainer}>
        <div>
          <Checkbox
            label='Limit to national standards'
            checked={limitToNational}
            onChange={() => dispatch({ type: TOGGLE_LIMIT_TO_NATIONAL })}
          />
          <Checkbox
            label='Only Crosswalks'
            checked={onlyCrosswalks}
            onChange={() => dispatch({ type: TOGGLE_ONLY_CROSSWALKS })}
          />
        </div>
        <div
          css={$clearFilters}
          onClick={() => dispatch({ type: CLEAR_ALL_FILTERS })}
        >
          Clear all filters
        </div>
      </div>
      <div css={{ display: 'flex', gap: '8px' }}>
        <div>
          <CustomSelect
            name='adoption year'
            applyLabel='Apply time range'
            value={adoptionYearRange}
            onChange={(val) => dispatch({ type: SET_FILTER_RANGE, payload: val })}
            placeholder='Select adoption year'
            pendingValue={pendingAdoptionYearRange}
            setPendingValue={setPendingAdoptionYearRange}
            hasError={!pendingValidation.isValid}
            errorMessage={pendingValidation.error}
            customDropdownBody={({ pendingValue, setPendingValue, hasError, errorMessage }) => (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 15, alignContent: 'center', padding: '8px 8px' }}>
                  <div css={$yearInputContainer}>
                    <Input
                      label='Earliest year'
                      onChange={(e) => setPendingValue({ ...pendingValue, from: e.target.value })}
                      placeholder='e.g. 2025'
                      value={pendingValue.from || ''}
                      inputType='number'
                      __cssFor={{
                        input: [
                          $yearInput,
                          hasError && { borderColor: ERROR_COLOR }
                        ],
                        requiredAsterisk: { ...generateIconColorCss({ primaryColor: 'transparent' }) }
                      }}
                    />
                  </div>
                  <div css={$to}>to</div>
                  <div css={$yearInputContainer}>
                    <Input
                      label='Latest year'
                      onChange={(e) => setPendingValue({ ...pendingValue, to: e.target.value })}
                      placeholder='e.g. 2025'
                      value={pendingValue.to || ''}
                      inputType='number'
                      __cssFor={{
                        input: [
                          $yearInput,
                          hasError && { borderColor: ERROR_COLOR }
                        ],
                        requiredAsterisk: { ...generateIconColorCss({ primaryColor: 'transparent' }) }
                      }}
                    />
                  </div>
                </div>
                {hasError && errorMessage && (
                  <div css={$errorMessage}>
                    {errorMessage}
                  </div>
                )}
              </div>
            )}
            getLabel={() => getRangeLabel(adoptionYearRange, 'Select adoption year')}
          />
        </div>
        <div css={{ maxWidth: 200, flex: '1 1 auto' }}>
          <CustomSelect
            name='subject'
            isMulti
            options={SUBJECT_OPTIONS}
            value={subjects}
            onChange={(val) => dispatch({ type: SET_FILTER_SUBJECTS, payload: val })}
            placeholder='Select subject'
            getLabel={() => getSelectedLabel(subjects, 'subject', 'subjects', 'Select subject')}
          />
        </div>
        <div css={{ maxWidth: 200, flex: '1 1 auto' }}>
          <CustomSelect
            name='grade'
            isMulti
            options={GRADE_OPTIONS}
            value={grades}
            onChange={(val) => dispatch({ type: SET_FILTER_GRADES, payload: val })}
            placeholder='Select grade'
            getLabel={() => getSelectedLabel(grades, 'grade', 'grades', 'Select grade')}
          />
        </div>
        <div css={{ maxWidth: 200, flex: '1 1 auto' }}>
          <CustomSelect
            name='state'
            isMulti
            options={STATE_OPTIONS}
            value={states}
            onChange={(val) => dispatch({ type: SET_FILTER_STATES, payload: val })}
            placeholder='Select state'
            getLabel={() => getSelectedLabel(states, 'state', 'states', 'Select state')}
          />
        </div>
      </div>
    </div>
  );
};

Filters.propTypes = {
  dispatch: PropTypes.func.isRequired,
  state: PropTypes.shape({
    search: PropTypes.string,
    limitToNational: PropTypes.bool,
    onlyCrosswalks: PropTypes.bool,
    adoptionYearRange: PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string,
    }),
    subjects: PropTypes.array,
    grades: PropTypes.array,
    states: PropTypes.array,
  }).isRequired,
};

export default Filters;
